import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase, AnimeCharacter } from '../../../lib/supabase';
import Cropper from 'react-easy-crop';
import { Area } from 'react-easy-crop/types';

type AvatarEditorProps = {
  onClose: () => void;
  onSave: () => void;
};

export default function AvatarEditor({ onClose, onSave }: AvatarEditorProps) {
  const { user } = useAuth();
  const [characters, setCharacters] = useState<AnimeCharacter[]>([]);
  const [selectedCharacter, setSelectedCharacter] = useState<AnimeCharacter | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadCharacters();
  }, []);

  const loadCharacters = async () => {
    const allCharacters: AnimeCharacter[] = [];
    const pageSize = 1000;
    let page = 0;
    let hasMore = true;

    while (hasMore) {
      const from = page * pageSize;
      const to = from + pageSize - 1;

      const { data } = await supabase
        .from('anime_characters')
        .select('*')
        .order('character_name', { ascending: true })
        .range(from, to);

      if (data && data.length > 0) {
        allCharacters.push(...data);
        if (data.length < pageSize) {
          hasMore = false;
        } else {
          page++;
        }
      } else {
        hasMore = false;
      }
    }

    setCharacters(allCharacters);
    setLoading(false);
  };

  const onCropComplete = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleSave = async () => {
    if (!user || !selectedCharacter || !croppedAreaPixels) return;

    setSaving(true);

    // Save the cropped area pixels which we'll use to display the avatar correctly
    const { error } = await supabase
      .from('profiles')
      .update({
        avatar_character_id: selectedCharacter.id,
        avatar_url: selectedCharacter.image_url,
        avatar_zoom: {
          x: croppedAreaPixels.x,
          y: croppedAreaPixels.y,
          width: croppedAreaPixels.width,
          height: croppedAreaPixels.height,
          scale: zoom,
        },
      })
      .eq('id', user.id);

    setSaving(false);

    if (!error) {
      setShowSuccess(true);
      setTimeout(() => {
        onSave();
        onClose();
      }, 1000);
    } else {
      console.error('Error saving avatar:', error);
      alert('Failed to save avatar. Please try again.');
    }
  };

  const filteredCharacters = characters.filter(
    (char) =>
      char.character_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      char.anime_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex-shrink-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Choose Avatar</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {!selectedCharacter ? (
            <div>
              <p className="text-sm text-gray-600 mb-4">
                Select a character to use as your avatar
              </p>

              {/* Search Bar */}
              <div className="mb-4">
                <div className="relative">
                  <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-base md:text-lg"></i>
                  <input
                    type="text"
                    placeholder="Search characters or anime..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-11 md:pl-12 pr-4 py-2.5 md:py-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              {loading ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-pink-500 border-t-transparent"></div>
                </div>
              ) : filteredCharacters.length === 0 ? (
                <div className="text-center py-12">
                  <i className="ri-search-line text-5xl md:text-6xl text-gray-300 mb-4"></i>
                  <p className="text-xs md:text-sm text-gray-500">No characters found</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {filteredCharacters.map((character) => (
                    <button
                      key={character.id}
                      onClick={() => setSelectedCharacter(character)}
                      className="group cursor-pointer"
                    >
                      <div className="aspect-[3/4] rounded-lg overflow-hidden shadow-md group-hover:shadow-xl transition-shadow">
                        <img
                          src={character.image_url}
                          alt={character.character_name}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <p className="mt-2 text-xs font-medium text-gray-700 text-center truncate">
                        {character.character_name}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="mb-4">
                <button
                  onClick={() => setSelectedCharacter(null)}
                  className="text-sm font-medium text-pink-500 hover:text-pink-600 cursor-pointer whitespace-nowrap"
                >
                  ← Choose Different Character
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Adjust the position and zoom of your avatar
              </p>

              <div className="relative h-96 bg-gray-100 rounded-lg overflow-hidden mb-6">
                <Cropper
                  image={selectedCharacter.image_url}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                  cropShape="round"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Zoom
                </label>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="w-full"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 py-3 text-base font-semibold text-white bg-gradient-to-r from-pink-500 to-purple-600 rounded-full hover:shadow-lg transition-all whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  {saving ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="inline-block animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Saving...
                    </span>
                  ) : showSuccess ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-check-line"></i>
                      Saved!
                    </span>
                  ) : (
                    'Save Avatar'
                  )}
                </button>
                <button
                  onClick={onClose}
                  disabled={saving}
                  className="px-8 py-3 text-base font-semibold text-gray-700 bg-white border-2 border-gray-300 rounded-full hover:border-gray-400 hover:shadow-md transition-all whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
